import { signalStore, withMethods, withState } from '@ngrx/signals';
import { updateState, withDevtools } from '@angular-architects/ngrx-toolkit';
import { Conversation, ConversationIncludeOption, ConversationPostRequest } from '@clientwire/api';
import { inject } from '@angular/core';
import {
  removeAllEntities,
  removeEntity,
  setAllEntities,
  setEntities,
  updateEntity,
  withEntities,
} from '@ngrx/signals/entities';
import { ConversationFilters } from '../interfaces';
import { WireApiService } from '../services';
import { logger } from '../utils';
import { ErrorStore, ResponseError } from './error.store';

export const ConversationStore = signalStore(
  { providedIn: 'root', protectedState: false },
  withDevtools('conversation-store'),
  withState({
    filterString: '',
    showArchiveOnly: false,
    conversationTypeIds: [] as string[],
    error: null,
    loadingConversations: false,
    totalElements: 0,
    hiddenConversationCount: 0,
    hiddenConversationIds: [] as string[],
  }),
  withEntities<Conversation>(),
  withMethods((store) => {
    const wireApiService = inject(WireApiService);
    const errorStore = inject(ErrorStore);

    return {
      async getConversations(limit = 10, offset = 0) {
        updateState(store, 'Set loadingConversation to true', { loadingConversations: true });

        errorStore.clearErrorsByLocations(['getConversations']);

        const response = await wireApiService.conversationsApi
          .getConversations({
            limit,
            offset,
            archived: store.showArchiveOnly(),
            search: store.filterString(),
            conversationType:
              store.conversationTypeIds().length > 0
                ? Array.from(store.conversationTypeIds())
                : undefined,
            includes: [
              ConversationIncludeOption.Participants,
              ConversationIncludeOption.ConversationType,
              ConversationIncludeOption.ListItemTemplateData,
              ConversationIncludeOption.ClientParticipantFrontendUrl,
              ConversationIncludeOption.ParticipantNotificationStatus,
            ],
          })
          .catch((err) => {
            console.error('BEFORE LOGIN ERROR:', err);
            errorStore.addErrorResponse(err, 'inline', false, 'getConversations');
            updateState(store, 'Set loadingConversation to false', { loadingConversations: false });
            return err;
          });

        if (response.conversations?.length > 0) {
          if (offset === 0) {
            updateState(
              store,
              'Set all conversations in ConversationStore',
              setAllEntities(response.conversations)
            );
          } else {
            updateState(
              store,
              'Set conversations in ConversationStore',
              setEntities(response.conversations)
            );
          }
        } else if (response.totalElements === 0) {
          updateState(store, 'Remove all conversation from ConversationStore', removeAllEntities());
        }
        updateState(store, 'Set totalElements of ConversationStore', {
          totalElements: response.totalElements ?? 0,
        });
        updateState(store, 'Set loadingConversation to false', { loadingConversations: false });
      },

      async getConversation(conversationId: string, includes: ConversationIncludeOption[]) {
        errorStore.clearErrorsByLocations([`getConversation:${conversationId}`]);
        try {
          const response = await wireApiService.conversationsApi.getConversation({
            conversationId,
            includes,
          });
          updateState(
            store,
            'Add conversation to ConversationStore',
            setAllEntities([response, ...store.entities()])
          );
        } catch (error) {
          try {
            await errorStore.addErrorResponse(
              error,
              'global',
              true,
              `getConversation:${conversationId}`,
              'error.global.title',
              'error.conversation.get',
              'error.conversation.get'
            );
          } catch {
            await errorStore.addErrorResponse(
              { code: `error.conversation.get` },
              'global',
              false,
              `getConversation:${conversationId}`
            );
          }
        }
      },

      clearError() {
        updateState(store, 'Clear errors in ConversationStore', { error: null });
      },

      /**
       * Checks if a conversation matches the provided search string.
       * Searches in participants (display name, email, phone number, CLIENT id) and conversation data.
       */
      conversationMatchesSearch(conversation: Conversation, searchString: string): boolean {
        const filterString = searchString?.toLowerCase().trim() || '';

        if (!filterString) {
          return true; // No search string means all conversations match
        }

        // Check participants for matches
        if (conversation.participants && conversation.participants.length > 0) {
          for (const participant of conversation.participants) {
            const displayNameMatch = participant.displayName?.toLowerCase().includes(filterString);
            const emailMatch = participant.email?.toLowerCase().includes(filterString);
            const phoneNumberMatch = participant.phoneNumber?.toLowerCase().includes(filterString);

            // Check participant ID if kind is CLIENT
            const isClient = participant.kind?.toString().toLowerCase() === 'client';
            const idMatch =
              isClient && participant.id?.toString().toLowerCase().includes(filterString);

            if (displayNameMatch || emailMatch || phoneNumberMatch || idMatch) {
              return true;
            }
          }
        }

        // Check conversation_data for matches if not already matched
        if (conversation.conversationData) {
          const checkValue = (value: unknown): boolean => {
            if (value === null || value === undefined) {
              return false;
            }
            if (typeof value === 'string') {
              return value.toLowerCase().includes(filterString);
            }
            if (typeof value === 'number' || typeof value === 'boolean') {
              return value.toString().toLowerCase().includes(filterString);
            }
            if (Array.isArray(value)) {
              return value.some((item) => checkValue(item));
            }
            if (typeof value === 'object') {
              return Object.values(value).some((val) => checkValue(val));
            }
            return false;
          };

          return Object.values(conversation.conversationData).some((value) => checkValue(value));
        }

        return false;
      },

      /**
       * Checks if a conversation passes the current active filters
       */
      conversationPassesFilters(conversation: Conversation): boolean {
        // Check archive filter
        const passesArchiveFilter = store.showArchiveOnly()
          ? conversation.archived
          : !conversation.archived;

        // Check conversation type filter
        const passesTypeFilter =
          store.conversationTypeIds().length === 0 ||
          store.conversationTypeIds().includes(conversation.conversationTypeId);

        // Check search filter
        const passesSearchFilter = this.conversationMatchesSearch(
          conversation,
          store.filterString()
        );

        return passesArchiveFilter && passesTypeFilter && passesSearchFilter;
      },

      /**
       * Increments hidden conversation count for a conversation that doesn't pass filters.
       * Tracks conversation ID to prevent double-counting (e.g., manual creation + WebSocket).
       */
      incrementHiddenConversationCount(conversationId: string): void {
        // Only increment if we haven't already counted this conversation as hidden
        if (!store.hiddenConversationIds().includes(conversationId)) {
          updateState(store, 'Increment hidden conversation count', {
            hiddenConversationCount: store.hiddenConversationCount() + 1,
            hiddenConversationIds: [...store.hiddenConversationIds(), conversationId],
          });
        }
      },

      async createNewConversation(
        newConversation: ConversationPostRequest,
        includes: ConversationIncludeOption[]
      ): Promise<Conversation | null> {
        try {
          errorStore.clearErrorsByLocations(['createConversation']);
          const response = await wireApiService.conversationsApi.createConversation({
            conversationPostRequest: newConversation,
            includes,
          });

          if (this.conversationPassesFilters(response)) {
            // Conversation passes filters, add it to the list
            updateState(
              store,
              'Add conversation to ConversationStore',
              setAllEntities([response, ...store.entities()])
            );
          } else {
            // Conversation doesn't pass filters, track it as hidden
            this.incrementHiddenConversationCount(response.id);
          }

          return response;
        } catch (err) {
          const jsonResponse = await (err as ResponseError).response?.json();
          const errorMessage = jsonResponse?.message;
          if (errorMessage) {
            errorStore.addErrorResponse(jsonResponse, 'inline', false, 'createConversation');
          } else {
            logger.error(err);
          }
          return null;
        }
      },

      addConversation(conversation: Conversation, sort: 'asc' | 'desc' = 'desc') {
        const conversations = store.entities();
        if (conversations.find((c) => c.id === conversation.id)) {
          return;
        }
        const index = conversations.findIndex((c) => {
          if (!c.createdAt || !conversation.createdAt) {
            return false;
          }
          if (sort === 'asc') {
            return c.createdAt > conversation.createdAt;
          } else {
            return c.createdAt < conversation.createdAt;
          }
        });

        if (index === -1) {
          conversations.push(conversation);
        } else {
          conversations.splice(index, 0, conversation);
        }

        updateState(store, 'Add conversation to ConversationStore', setAllEntities(conversations));
      },

      changeFilters(filters: ConversationFilters) {
        updateState(store, 'Change filters in ConversationStore', {
          showArchiveOnly: filters.showArchived,
          filterString: filters.search,
          conversationTypeIds: filters.conversationTypes ?? [],
        });
      },

      resetFilters() {
        updateState(store, 'Reset filters to default in ConversationStore', {
          showArchiveOnly: false,
          filterString: '',
          conversationTypeIds: [],
          hiddenConversationCount: 0,
          hiddenConversationIds: [],
        });
      },

      clearHiddenConversationCount() {
        updateState(store, 'Clear hidden conversation count in ConversationStore', {
          hiddenConversationCount: 0,
          hiddenConversationIds: [],
        });
      },

      async archiveConversation(id: string) {
        await wireApiService.conversationsApi.updateConversation({
          conversationId: id,
          conversationPatchRequest: { archived: true },
          includes: [],
        });

        updateState(
          store,
          'Update conversation in ConversationStore',
          updateEntity({ id, changes: { archived: true } })
        );
      },

      async unarchiveConversation(id: string) {
        await wireApiService.conversationsApi.updateConversation({
          conversationId: id,
          conversationPatchRequest: { archived: false },
          includes: [],
        });

        updateState(
          store,
          'Update conversation in ConversationStore',
          updateEntity({ id, changes: { archived: false } })
        );
      },

      updateConversation(conversationId: string, changes: Partial<Conversation>) {
        updateState(
          store,
          'Update conversation in ConversationStore',
          updateEntity({ id: conversationId, changes: { ...changes } })
        );
      },

      removeConversation(conversationId: string) {
        updateState(
          store,
          'Remove conversation from ConversationStore',
          removeEntity(conversationId)
        );
      },

      clearConversations() {
        updateState(store, 'Clear ConversationStore', {
          filterString: '',
          showArchiveOnly: false,
          conversationTypeIds: [],
          error: null,
          loadingConversations: false,
          totalElements: 0,
          hiddenConversationCount: 0,
          hiddenConversationIds: [],
        });
        updateState(store, 'Clear entities', removeAllEntities());
      },
    };
  })
);
